"use client"

import React, {useRef, useState} from 'react';
import styles from "./Pray.module.scss";
import {useTranslations} from "use-intl";
import Prayers from "@/components/Prayers/Prayers";
import Liturgies from "@/components/Liturgies/Liturgies";
import Spirituality from "@/components/Spirituality/Spirituality";
import Blessings from "@/components/Blessings/Blessings";
import Saints from "@/components/Saints/Saints";
import {SimpleTreeView} from '@mui/x-tree-view/SimpleTreeView';
import PrayersItems from "@/components/Prayers/PrayersItems";
import LiturgiesItems from "@/components/Liturgies/LiturgiesItems";
import SpiritualityItems from "@/components/Spirituality/SpiritualityItems";
import BlessingsItems from "@/components/Blessings/BlessingsItems";
import SaintsItems from "@/components/Saints/SaintsItems";
import LoginAlert from "@/components/LoginAlert/LoginAlert";
import {useAppStore} from "@/store/AppStore";
import {Accordion, AccordionDetails, AccordionSummary, Button, ButtonGroup} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {Link} from "@/i18n/routing";
import {useSearchParams} from "next/navigation";

export default function Pray() {
  const t = useTranslations("Pray")
  const searchParams = useSearchParams()
  const user = useAppStore((state) => state.user);

  // Deep-link iniziale via ?saint=<id> o ?reading=<id>: usiamo lazy state
  // init per selezionare automaticamente il santo / la lettura spirituale
  // corrispondente e aprire il relativo accordion (4 per i santi, 2 per le
  // letture). Successivi cambi di URL dentro la stessa sessione
  // non sovrascrivono lo stato del componente: in caso di nuova URL
  // l'utente vede comunque l'app montata, ed e' il browser a guidare
  // la navigazione (no setState in effect).
  const initialSaint = searchParams.get('saint')
  const initialReading = searchParams.get('reading')
  const [active, setActive] = useState(() => {
    if (initialSaint) return `saint-${initialSaint}`
    if (initialReading) return `spirituality-${initialReading}`
    return '0'
  })
  const [expanded, setExpanded] = useState<string | false>(() => {
    if (initialSaint) return '4'
    if (initialReading) return '2'
    return ''
  })
  const contentRef = useRef<HTMLDivElement>(null);

  const handleAppLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, url: string) => {
    e.preventDefault();
    const link = document.createElement('a');
    link.href = url;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      window.location.href = "https://banchedati.chiesacattolica.it/appliturgia/";
      document.body.removeChild(link);
    }, 1500);
  }

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  const handleItemSelectionToggle = (
    event: React.SyntheticEvent | null,
    itemId: string,
    isSelected: boolean,
  ) => {
    if (isSelected) {
      setActive(itemId)
      if (contentRef.current && itemId.includes("-")) {
        contentRef.current.scrollIntoView({behavior: "smooth", block: "start", inline: "nearest"});
      }
    }
  };
  if (!user) return <div className={styles.container}>
    <div className={styles.wrapper}><LoginAlert/></div>
  </div>
  return (
    <div className={styles.container}>
      <div className={styles.treeWrapper}>
        <ButtonGroup className={styles.liturgiaCei} orientation={"vertical"} variant="contained">
          <Link
            href={"liturgiacei://liturgy"}
            onClick={(e) => {
              handleAppLinkClick(e, "liturgiacei://liturgy")
            }}
          >
            <Button variant="outlined">Liturgia del giorno</Button>
          </Link>
          <Link href={"liturgiacei://mass"}
                onClick={(e) => {
                  handleAppLinkClick(e, "liturgiacei://mass")
                }}
          >
            <Button variant="outlined">Messa del giorno</Button>
          </Link>
          <Link href={"liturgiacei://saint"}
                onClick={(e) => {
                  handleAppLinkClick(e, "liturgiacei://saint")
                }}
          >
            <Button variant="outlined">Santo del giorno</Button>
          </Link>
        </ButtonGroup>

        <Accordion style={{width: "100%"}} id={"0"} expanded={expanded === '0'} onChange={handleChange('0')}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon/>}
          >
            {t('prayers')}
          </AccordionSummary>
          <AccordionDetails>
            <SimpleTreeView style={{width: "100%"}} onItemSelectionToggle={handleItemSelectionToggle}>
              <PrayersItems/>
            </SimpleTreeView>
          </AccordionDetails>
        </Accordion>
        <Accordion style={{width: "100%"}} id={"1"} expanded={expanded === '1'} onChange={handleChange('1')}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon/>}
          >
            {t('liturgies')}
          </AccordionSummary>
          <AccordionDetails>
            <SimpleTreeView style={{width: "100%"}} onItemSelectionToggle={handleItemSelectionToggle}>
              <LiturgiesItems/>
            </SimpleTreeView>
          </AccordionDetails>
        </Accordion>
        <Accordion style={{width: "100%"}} id={"2"} expanded={expanded === '2'} onChange={handleChange('2')}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon/>}
          >
            {t('spirituality')}
          </AccordionSummary>
          <AccordionDetails>
            <SimpleTreeView style={{width: "100%"}} onItemSelectionToggle={handleItemSelectionToggle}>
              <SpiritualityItems/>
            </SimpleTreeView>
          </AccordionDetails>
        </Accordion>
        <Accordion style={{width: "100%"}} id={"3"} expanded={expanded === '3'} onChange={handleChange('3')}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon/>}
          >
            {t('blessings')}
          </AccordionSummary>
          <AccordionDetails>
            <SimpleTreeView style={{width: "100%"}} onItemSelectionToggle={handleItemSelectionToggle}>
              <BlessingsItems/>
            </SimpleTreeView>
          </AccordionDetails>
        </Accordion>
        <Accordion style={{width: "100%"}} id={"4"} expanded={expanded === '4'} onChange={handleChange('4')}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon/>}
          >
            {t('saints')}
          </AccordionSummary>
          <AccordionDetails>
            <SimpleTreeView style={{width: "100%"}} onItemSelectionToggle={handleItemSelectionToggle}>
              <SaintsItems/>
            </SimpleTreeView>
          </AccordionDetails>
        </Accordion>
      </div>
      <div className={styles.wrapper}>
        <div ref={contentRef}>
          <Prayers currentId={active}/>
          <Liturgies currentId={active}/>
          <Spirituality currentId={active}/>
          <Blessings currentId={active}/>
          <Saints currentId={active}/>
        </div>
      </div>
    </div>
  )
}
